/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React from 'react';
import { Button, Card, Input, InputRef, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BanEntity } from '../../services/api/types/users';
import { apiBansList, apiRemoveBan } from '../../services/api/users';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import { useNavigate } from 'react-router';
import { RouterPaths } from '../../router/paths';
import { useSearchParams } from 'react-router-dom';
import { debounce } from '../../helpers';

interface DataType extends BanEntity {
  key: string;
}

const PER_PAGE_LIMIT = 10;

function BansPage() {
  const [params, setSearchParams] = useSearchParams({ pageOffset: '0' });
  const pageOffset = params.get('pageOffset');
  const [paging, setPaging] = React.useState<{ offset: number; limit: number }>(
    {
      offset: pageOffset ? parseInt(pageOffset) : 0,
      limit: PER_PAGE_LIMIT,
    },
  );
  const [dataSource, setDataSource] = React.useState<
    [DataType[], number] | null
  >(null);
  const { token } = useSelector(selectUserState);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef() as React.MutableRefObject<InputRef>;
  const [refresh, setRefresh] = React.useState<symbol>(Symbol('s'));

  React.useEffect(() => {
    if (token) {
      setLoading(true);
      (async () => {
        const response = await apiBansList(
          {
            offset: paging.offset,
            limit: paging.limit,
            search: searchValue?.length > 2 ? searchValue : undefined,
          },
          token,
        );
        if (response) {
          if (dataSource) {
            setDataSource([
              [
                ...dataSource[0],
                ...response.bans.map((usr) => ({
                  ...usr,
                  key: usr.id.toString(),
                })),
              ],
              response.total,
            ]);
          } else {
            setDataSource([
              response.bans.map((usr) => ({ ...usr, key: usr.id.toString() })),
              response.total,
            ]);
          }
          setDataSource([
            response.bans.map((usr) => ({ ...usr, key: usr.id.toString() })),
            response.total,
          ]);
        } else {
          setDataSource([[], 0]);
        }
        setLoading(false);
        setTimeout(() => {
          inputRef.current.focus();
        }, 100);
      })();
    }
  }, [paging, searchValue, refresh]);

  const columns = React.useMemo<ColumnsType<DataType>>(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        render: (v) => `${v}`,
        fixed: true,
      },
      {
        key: 'email',
        title: 'Email',
        dataIndex: 'email',
        fixed: true,
        render: (v) => `${v}`,
      },
      {
        key: 'createdAt',
        title: 'Created at',
        dataIndex: 'createdAt',
        render: (v) => `${v}`,
      },
      {
        key: 'actions',
        title: 'Actions',
        dataIndex: 'createdAt',
        render: (v, record) => (
          <Button
            danger
            onClick={async () => {
              try {
                if (token) {
                  const res = await apiRemoveBan(
                    {
                      id: record.aliasId,
                    },
                    token,
                  );

                  if (res) {
                    setRefresh(Symbol('s'));
                  }
                }
              } catch (e) {
                console.error('Error removing ban:', e);
              }
            }}
          >
            Remove
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Card bordered={false} title="Users">
      <div
        style={{ marginBottom: 25, display: 'flex', flexDirection: 'column' }}
      >
        <span style={{ marginBottom: 15 }}>
          <strong>Total:</strong>
          {dataSource?.[0]?.length}
        </span>
        <Input
          placeholder="Search"
          type="text"
          onChange={debounce((e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchValue(e.target.value);
          }, 1000)}
          style={{ width: 'fit-content', marginBottom: 10 }}
          disabled={loading}
          ref={inputRef}
        />
      </div>
      {dataSource ? (
        <>
          <Table
            loading={loading}
            scroll={{ x: true }}
            dataSource={dataSource[0]}
            columns={columns}
            pagination={{
              total: dataSource[1],
              current: paging.offset / paging.limit + 1,
            }}
            onChange={(pagination) => {
              if (pagination.current) {
                const size = pagination.pageSize || PER_PAGE_LIMIT;
                const offset = size * (pagination.current - 1);
                setPaging({
                  offset,
                  limit: size,
                });
                setSearchParams(
                  {
                    pageOffset: offset.toString(),
                  },
                  { replace: true },
                );
              }
            }}
          />
        </>
      ) : null}
    </Card>
  );
}

export default BansPage;
